import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import SearchIcon from "@mui/icons-material/Search";
import entityMediaApi from "./entityMediaApi";

const SOUND_DIR = "/etc/coraltele/sounds";

export default function MediaCenterBrowser({
  open,
  onClose,
  onSelect,
  apiAdapter,
}) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [search, setSearch] = useState("");
  const [playingPath, setPlayingPath] = useState(null);
  const audioRef = useRef(null);
  const api = apiAdapter || entityMediaApi;

  const loadMediaCenter = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getMediaCenterFiles();
      const payload = response?.data;
      if (!payload) {
        setGroups([]);
        return;
      }

      const config =
        payload.config && typeof payload.config === "object"
          ? payload.config
          : payload;
      const headerItems = Array.isArray(config.headerItems)
        ? config.headerItems
        : [];

      const groupMap = {};
      for (const item of headerItems) {
        const type = item.type || "Other";
        const files = Array.isArray(item.properties)
          ? item.properties
              .filter((p) => p.audioFileName)
              .map((p) => ({
                fileName: p.audioFileName,
                filePath: buildAbsolutePath(item, p),
                language: p.language || "default",
              }))
          : [];
        if (files.length === 0) continue;
        if (!groupMap[type]) {
          groupMap[type] = { id: type, name: type, files: [] };
        }
        groupMap[type].files.push(...files);
      }

      setGroups(Object.values(groupMap));
    } catch (err) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) {
      loadMediaCenter();
      setSearch("");
      setPlayingPath(null);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [open, loadMediaCenter]);

  const buildAbsolutePath = (item, property) => {
    const lang = property.language || "default";
    if (item.type === "MOH") {
      return SOUND_DIR + "/music/8000/" + property.audioFileName;
    }
    if (item.isSystemPrompt) {
      return SOUND_DIR + "/en/us/callie/ivr/" + property.audioFileName;
    }
    if (lang === "default") {
      return SOUND_DIR + "/default-language/" + property.audioFileName;
    }
    return SOUND_DIR + "/" + lang + "/" + property.audioFileName;
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handlePlay = useCallback(
    async (filePath) => {
      if (playingPath === filePath) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setPlayingPath(null);
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      try {
        const blob = await api.fetchAudioBlob(filePath);
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        setPlayingPath(filePath);

        audio.onended = () => {
          setPlayingPath(null);
          URL.revokeObjectURL(url);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setPlayingPath(null);
          URL.revokeObjectURL(url);
          audioRef.current = null;
        };

        audio.play();
      } catch (err) {
        setPlayingPath(null);
      }
    },
    [api, playingPath]
  );

  const searchLower = search.toLowerCase();
  const filteredGroups = groups
    .map((g) => ({
      ...g,
      files: g.files.filter(
        (f) =>
          f.fileName.toLowerCase().includes(searchLower) ||
          g.name.toLowerCase().includes(searchLower)
      ),
    }))
    .filter((g) => g.files.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select from Media Center</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search files..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No media files found.
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 400, overflow: "auto" }}>
            {filteredGroups.map((group) => (
              <React.Fragment key={group.id}>
                <ListItemButton onClick={() => toggleGroup(group.id)}>
                  <ListItemText
                    primary={group.name}
                    secondary={`${group.files.length} file(s)`}
                  />
                  {expandedGroups[group.id] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </ListItemButton>
                <Collapse in={expandedGroups[group.id]}>
                  <List dense disablePadding>
                    {group.files.map((file) => (
                      <ListItemButton
                        key={file.filePath}
                        sx={{ pl: 4 }}
                        onClick={() => onSelect(file.filePath)}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <AudioFileIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.fileName}
                          secondary={file.language}
                        />
                        <Tooltip
                          title={
                            playingPath === file.filePath ? "Stop" : "Play"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlay(file.filePath);
                            }}
                          >
                            {playingPath === file.filePath ? (
                              <StopIcon fontSize="small" />
                            ) : (
                              <PlayArrowIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
